import { ResourceState } from "../../shared/ui/resource-state.js";
import type { LiveExecution } from "./live-execution-models.js";

const actionLabels: Readonly<Record<string, string>> = {
  "enter-long": "Buy to open long",
  "exit-long": "Sell to close long",
  "enter-short": "Sell to open short",
  "exit-short": "Buy to close short",
};

export function LiveExecutions({
  rows,
  selectedFillId,
  onSelect,
  loading,
  error,
  accountNames,
  botNames,
}: {
  readonly rows: readonly LiveExecution[];
  readonly selectedFillId?: string;
  readonly onSelect: (fillId: string) => void;
  readonly loading: boolean;
  readonly error: boolean;
  readonly accountNames: ReadonlyMap<string, string>;
  readonly botNames: ReadonlyMap<string, string>;
}) {
  return (
    <section
      className="content-card section-stack chart-evidence-panel"
      aria-label="Live executions"
    >
      <div className="connection-heading">
        <div>
          <h2>Executions</h2>
          <p className="section-description">
            Broker-confirmed TradingApp fills for this symbol. Select a row to focus the chart;
            orders placed directly in Tastytrade are not included.
          </p>
        </div>
      </div>
      {loading ? (
        <ResourceState
          kind="loading"
          title="Loading executions"
          description="Reading filled TradingApp orders."
        />
      ) : null}
      {error ? (
        <ResourceState
          kind="error"
          title="Executions unavailable"
          description="TradingEngine could not load the fill history."
        />
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No executions"
          description="No TradingApp broker-confirmed fills were found for this symbol."
        />
      ) : null}
      {rows.length > 0 ? (
        <div className="evidence-table-scroll">
          <table className="evidence-table">
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Action</th>
                <th scope="col">Shares</th>
                <th scope="col">Price</th>
                <th scope="col">Account</th>
                <th scope="col">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  aria-selected={row.fillId === selectedFillId}
                  key={row.fillId}
                  onClick={() => onSelect(row.fillId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(row.fillId);
                    }
                  }}
                  tabIndex={0}
                >
                  <td>
                    <time dateTime={row.occurredAt}>
                      {new Date(row.occurredAt).toLocaleString()}
                    </time>
                  </td>
                  <td>{actionLabels[row.action] ?? row.action.replaceAll("-", " ")}</td>
                  <td>{row.quantity}</td>
                  <td>${row.priceUsd}</td>
                  <td>{accountNames.get(row.accountId) ?? row.accountId}</td>
                  <td>
                    {row.origin === "manual" ? "Manual" : (botNames.get(row.botId ?? "") ?? "Bot")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
